import React, { useMemo, useState } from "react";
import { Check, Plane } from "lucide-react";

const JMD_PER_USD = 155;

const TIERS = [
  {
    id: "essential",
    name: "Essential",
    subtitle: "Rental-focused relocation",
    rangeJMD: [200000, 300000],
    pricingNote: "One-time fee",
    bestFor: "Short or long-term rental placement",
    features: [
      "Pre-relocation lifestyle consultation",
      "Curated parish & neighborhood guidance",
      "Verified landlord & agent referrals",
      "Lease coordination & utility setup",
      "Pre-arrival move-in inspection & cleaning",
      "Initial grocery stocking",
      "Optional transition to monthly care plan",
    ],
    accent: false,
  },
  {
    id: "premium",
    name: "Premium",
    subtitle: "Property purchase & safety setup",
    rangeJMD: [350000, 500000],
    pricingNote: "One-time fee",
    bestFor: "Investing in property acquisition in Jamaica",
    features: [
      "Comprehensive mobility & healthcare assessment",
      "Vetted real-estate lawyer & agent referrals",
      "Senior-suitability property evaluations",
      "Plain-language legal & real-estate summaries",
      "Post-purchase deep cleaning & utility hookup",
      "Senior-safety upgrades (grab bars, lighting)",
      "Multi-party family coordination (local & overseas)",
    ],
    accent: true,
    badge: "Most chosen",
  },
  {
    id: "elite",
    name: "Elite",
    subtitle: "Full settlement & lifestyle continuity",
    rangeJMD: [600000, 850000],
    pricingNote: "One-time + monthly care option",
    bestFor: "Complete white-glove relocation experience",
    features: [
      "Everything in Premium",
      "Personalized airport arrival & private transfer",
      "First-week immersive settlement concierge",
      "Medical, pharmacy & community registration",
      "30 days of priority appointment scheduling",
      "Local orientation: banking & essentials",
      "Monthly wellness check-ins for 90 days",
    ],
    accent: false,
  },
];

function fmtJMD(n) {
  return `$${n.toLocaleString("en-US")} JMD`;
}
function fmtUSD(n) {
  return `$${Math.round(n / JMD_PER_USD).toLocaleString("en-US")} USD`;
}

const PackageTierCard = ({ tier: t }) => (
  <article
    data-testid={`tier-${t.id}`}
    className={`relative rounded-2xl p-8 lg:p-10 flex flex-col transition-all duration-500 ${
      t.accent
        ? "tier-highlight lg:-translate-y-3"
        : "bg-white border border-gray-100 hover:shadow-md"
    }`}
  >
    {t.badge && (
      <span className="absolute -top-3 left-8 inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase font-medium bg-[var(--gg-primary)] text-white px-3 py-1.5 rounded-full">
        <Plane size={11} />
        {t.badge}
      </span>
    )}

    <header className="mb-7">
      <h3 className="font-serif text-3xl lg:text-4xl text-[var(--gg-deep)] tracking-tight">
        {t.name}
      </h3>
      <p className="text-sm text-[var(--gg-text-muted)] mt-2">{t.subtitle}</p>
    </header>

    <div className="mb-7">
      <div
        data-testid={`tier-price-${t.id}`}
        className="font-serif text-2xl lg:text-[28px] text-[var(--gg-primary)] leading-tight"
      >
        {t.displayPrice}
      </div>
      <p className="text-xs uppercase tracking-[0.18em] text-[var(--gg-text-muted)] mt-2">
        {t.pricingNote}
      </p>
      <p className="text-sm text-[var(--gg-text)] mt-4 italic">
        Best for: {t.bestFor}
      </p>
    </div>

    <ul className="space-y-3.5 mb-9 flex-1">
      {t.features.map((f) => (
        <li key={f} className="flex items-start gap-3 text-sm text-[var(--gg-text)]">
          <Check size={16} className="mt-1 text-[var(--gg-primary)] flex-shrink-0" strokeWidth={2} />
          <span>{f}</span>
        </li>
      ))}
    </ul>

    <a
      href="#contact"
      data-testid={`tier-cta-${t.id}`}
      className={`block text-center rounded-full py-3.5 text-sm tracking-wide transition-colors ${
        t.accent
          ? "bg-[var(--gg-primary)] text-white hover:bg-[var(--gg-primary-dark)]"
          : "border border-[var(--gg-primary)]/30 text-[var(--gg-primary)] hover:bg-[var(--gg-primary)] hover:text-white"
      }`}
    >
      Begin with {t.name}
    </a>
  </article>
);

export default function PackagesSection() {
  const [currency, setCurrency] = useState("JMD");

  const tiers = useMemo(() => {
    return TIERS.map((t) => {
      const [lo, hi] = t.rangeJMD;
      const display =
        currency === "JMD"
          ? `${fmtJMD(lo)} – ${fmtJMD(hi)}`
          : `${fmtUSD(lo)} – ${fmtUSD(hi)}`;
      return { ...t, displayPrice: display };
    });
  }, [currency]);

  return (
    <section
      id="packages"
      data-testid="packages-section"
      className="relative py-28 md:py-36"
      style={{
        background:
          "linear-gradient(180deg, #F1F6F7 0%, #FDFDFD 100%)",
      }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10 mb-16">
          <div className="max-w-2xl">
            <span className="eyebrow">Relocation-to-Jamaica Packages</span>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[var(--gg-deep)] mt-5 leading-[1.05] text-balance">
              A trusted path home,
              <br />
              <em className="not-italic text-[var(--gg-primary)] font-normal">
                shaped around the life ahead.
              </em>
            </h2>
            <p className="mt-7 text-lg text-[var(--gg-text)] leading-relaxed">
              Three considered tiers for seniors and diaspora families. Each is
              advisory & coordination-based — never legal or brokerage counsel —
              and built on our network of vetted Jamaican professionals.
            </p>
          </div>

          <CurrencyToggle currency={currency} setCurrency={setCurrency} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-7">
          {tiers.map((t) => (
            <PackageTierCard key={t.id} tier={t} />
          ))}
        </div>

        <p className="text-xs text-[var(--gg-text-muted)] mt-10 text-center max-w-2xl mx-auto leading-relaxed">
          USD figures shown at approximate 155 JMD/USD reference. All services
          strictly advisory and coordination-based. Graceful Guidance Ltd. does
          not provide legal counsel, licensed real estate brokerage, or formal
          financial advisory services.
        </p>
      </div>
    </section>
  );
}

export function CurrencyToggle({ currency, setCurrency }) {
  return (
    <div
      data-testid="currency-toggle"
      className="inline-flex items-center bg-white border border-gray-200 rounded-full p-1 self-start"
    >
      {["JMD", "USD"].map((c) => (
        <button
          key={c}
          data-testid={`currency-${c}`}
          onClick={() => setCurrency(c)}
          className={`px-5 py-2 text-xs tracking-[0.2em] uppercase rounded-full transition-colors ${
            currency === c
              ? "bg-[var(--gg-primary)] text-white"
              : "text-[var(--gg-text-muted)] hover:text-[var(--gg-deep)]"
          }`}
        >
          {c}
        </button>
      ))}
    </div>
  );
}
